import { useState } from "react";
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { Icon } from "@/components/Icon";
import { PopupCloseButton } from "@/components/PopupClose";
import { Spinner } from "@/components/Spinner";
import { useExchangeQuote, useExchangeSubscription } from "@/api/hooks";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";
import { referralDebtText } from "@/lib/referralDebt";

/**
 * ExchangeSubscriptionModal (owner 2026-07-24) — обмін реф-балансу на дні
 * підписки за реф-тарифом (25₴/день). Owner: «люди тупі, треба розжувати» →
 * робимо максимально зрозуміло: великий візуал «баланс → дні», порівняння зі
 * звичайною ціною, розбивка з решткою + чіткі 3 кроки/примітки.
 */
const REGULAR_DAY_UAH = 33; // звичайна ціна (990/30) — для порівняння

function dayWord(n: number): string {
  return n === 1 ? "день" : n % 10 >= 2 && n % 10 <= 4 && (n < 10 || n > 20) ? "дні" : "днів";
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-ink/[0.05] last:border-0">
      <span className="text-ink-dim">{label}</span>
      <span className={accent ? "font-bold text-primary" : "font-semibold text-ink"}>
        {value}
      </span>
    </div>
  );
}

export function ExchangeSubscriptionModal({ onClose }: { onClose: () => void }) {
  const quote = useExchangeQuote(true);
  const mutation = useExchangeSubscription();
  const q = quote.data;
  const price = q?.day_price_uah ?? 25;
  const maxDays = q?.max_days ?? q?.days ?? 0;
  // Owner 2026-07-27: людина з великим балансом не мусить міняти все — вводить
  // скільки днів хоче. За замовчуванням — максимум (як було раніше).
  const [daysInput, setDaysInput] = useState<string>("");
  const wantDays = daysInput === "" ? maxDays : parseInt(daysInput, 10) || 0;
  const validDays = wantDays >= 1 && wantDays <= maxDays;
  const cost = validDays ? wantDays * price : 0;
  const remaining = (q?.balance_uah ?? 0) - cost;
  const enough = maxDays >= 1;
  const canExchange = enough && validDays && !mutation.isPending;

  const handleConfirm = async () => {
    if (!canExchange) return;
    try {
      await mutation.mutateAsync(wantDays);
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      // Owner 2026-07-27: свого попапа тут не показуємо — про активацію
      // повідомляє системний SubscriptionActivatedModal, інакше два попапи.
      onClose();
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert(`Помилка: ${(e as Error).message}`);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm bg-surface rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <PopupCloseButton onClose={onClose} />

        <div className="text-center mb-4">
          <div className="mb-2 flex justify-center">
            <TgEmoji char="💎" size={48} />
          </div>
          <div className="font-display text-[1.1875rem] font-bold text-ink leading-tight mb-1.5">
            Реферальні → підписка
          </div>
          <div className="text-[0.8125rem] text-ink-dim leading-relaxed">
            Гроші, які ти заробив на рефералах, можна обміняти на дні підписки —
            і це <b className="text-ink">дешевше</b>, ніж купувати.
          </div>
        </div>

        {quote.isLoading ? (
          <div className="py-8 flex justify-center">
            <Spinner />
          </div>
        ) : enough && q ? (
          <>
            {/* Головний візуал: баланс → дні */}
            <div className="rounded-2xl bg-primary/[0.06] ring-1 ring-primary/20 p-4 mb-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-center flex-1">
                  <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1">
                    Твій баланс
                  </div>
                  <div className="font-display text-[1.375rem] font-bold text-ink leading-none">
                    {q.balance_uah} ₴
                  </div>
                </div>
                <span className="text-primary shrink-0">
                  <Icon name="arrow-right" size={22} />
                </span>
                <div className="text-center flex-1">
                  <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1">
                    Отримаєш
                  </div>
                  <div className="font-display text-[1.375rem] font-bold text-primary leading-none">
                    {validDays ? `${wantDays} ${dayWord(wantDays)}` : "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Скільки днів брати (owner 2026-07-27): не обов'язково весь баланс */}
            <div className="rounded-2xl bg-bg/50 p-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1">
                    Скільки днів беру
                  </div>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={maxDays}
                    value={daysInput === "" ? String(maxDays) : daysInput}
                    // Owner 2026-08-10: більше, ніж дозволяє баланс, ввести не
                    // можна взагалі — `max` на мобільних не заважає набрати
                    // будь-яке число, тож ріжемо руками.
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        setDaysInput("");
                        return;
                      }
                      const n = parseInt(raw, 10);
                      if (Number.isNaN(n)) return;
                      setDaysInput(String(Math.min(Math.max(n, 0), maxDays)));
                    }}
                    className={
                      "w-full rounded-xl bg-surface px-3 py-2 font-display text-[1rem] " +
                      "font-bold text-ink outline-none ring-1 transition " +
                      (validDays ? "ring-ink/10 focus:ring-primary/40" : "ring-red-400")
                    }
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setDaysInput(String(maxDays))}
                  className="mt-4 shrink-0 rounded-xl bg-ink/[0.06] px-3 py-2 font-display text-[0.75rem] font-semibold text-ink active:bg-ink/10"
                >
                  Максимум
                </button>
              </div>
              <div className="mt-1.5 text-[0.6875rem] text-ink-faint whitespace-nowrap overflow-hidden text-ellipsis">
                {validDays
                  ? `Максимум — ${maxDays} ${dayWord(maxDays)}`
                  : `Введи від 1 до ${maxDays}`}
              </div>
            </div>

            {/* Розбивка — розжовано */}
            <div className="rounded-2xl bg-bg/50 px-3.5 py-1 text-[0.78125rem] mb-3">
              <Row
                label="Тариф обміну"
                value={`${price} ₴ = 1 день`}
                accent
              />
              <Row label="Звичайна ціна" value={`~${REGULAR_DAY_UAH} ₴ / день`} />
              <Row label="Спишеться з балансу" value={`${cost} ₴`} />
              <Row label="Решта лишиться" value={`${remaining} ₴`} />
            </div>

          </>
        ) : q && q.balance_uah < 0 ? (
          // Мінус після чарджбеку друга (owner 2026-10-22): обмін закритий.
          <div className="rounded-2xl bg-bg/50 p-5 text-center mb-2">
            <div className="text-[0.875rem] font-semibold text-ink mb-1">
              Реферальний баланс у мінусі
            </div>
            <div className="text-[0.78125rem] text-ink-dim leading-relaxed [text-wrap:balance]">
              {referralDebtText(q.balance_uah)}
            </div>
          </div>
        ) : (
          <div className="rounded-2xl bg-bg/50 p-5 text-center mb-2">
            <div className="text-[0.875rem] font-semibold text-ink mb-1">
              Ще замало грошей
            </div>
            <div className="text-[0.78125rem] text-ink-dim leading-relaxed">
              Мінімум <b className="text-ink">{price} ₴</b> (це 1 день). Зараз у
              тебе {q?.balance_uah ?? 0} ₴. Запрошуй друзів — і баланс зросте 😉
            </div>
          </div>
        )}

        {enough && q ? (
          <button
            type="button"
            disabled={!canExchange}
            onClick={handleConfirm}
            className={
              "mt-4 block w-full rounded-full py-3.5 font-display text-[0.9375rem] font-bold transition " +
              (canExchange
                ? "bg-primary text-white active:bg-primary-deep shadow-card-soft"
                : "bg-ink/10 text-ink-dim cursor-not-allowed")
            }
          >
            {mutation.isPending
              ? "Активую…"
              : validDays
                ? `Отримати ${wantDays} ${dayWord(wantDays)} підписки`
                : "Вкажи кількість днів"}
          </button>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
